import React, { useEffect, useState } from 'react';
import {
  Tabs,
  Tab,
  Box,
  Button,
  IconButton,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import VendorAndPayment from './VendorAndPayment';
import ReceivableAndShipping from './ReceivableAndShipping';
import ContactAndNotes from './ContactAndNotes';
import { buttonMarginSpace, buttonWidth } from '../../constants/form';
import { useFormik } from 'formik';
import { TAB_ERROR_PREFIXES, vendorSchema } from './schemas/indexSchema';
import { client } from '../../../../util';
import type { Vendor, VendorCreate, VendorTerms } from '@repo/api-contract';
import { pushToast, useDispatch, useSelector } from '../../../../state';
import {
  closeModal,
  fetchVendorToEdit,
  setLoading,
  setMode,
  VendorModalMode,
} from '../../../../state/vendorModalSlice';
import DraggablePaper from '../../DraggablePaper';
import { useTranslation } from 'react-i18next';
import { TRPCClientError } from '@trpc/client';
import { serverError } from '../../constants/errors';
import { serverLatencyMessage } from '../../constants/messages';
import {
  getTabsWithVisibleErrors,
  useFormikTabErrorMiddleware,
  tabErrorSx,
  tabIndicatorErrorSx,
  tabHasError,
} from '../../../utils/FormikTabErorrs';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

const initialValues: VendorCreate = {
  details: {
    companyName: '',
    accountNumber: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'US',
    employerIdentificationNumber: '',
    website: '',
    email: '',
    phone: '',
    preferredCommunication: '',
    other: '',
  },
  receivable: {
    companyName: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'US',
    contactName: '',
    email: '',
    phone: '',
    preferredCommunication: '',
  },
  shipping: {
    address: '',
    companyName: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'US',
    email: '',
    phone: '',
    contactName: '',
    preferredCommunication: '',
  },
  showNotes: false,
  payment: {
    minimum: 0,
    rebates: false,
    percentage: 0,
    vendorTerms: [],
    currentBalance: 0,
    lastOrderDate: 0,
    volume: 0,
  },
  notes: '',
  contacts: [],
};

export default function CreateEditVendorModal() {
  const { t: tGlobal } = useTranslation([], {
    keyPrefix: 'team.Global.Buttons',
  });
  const { t: tAddC } = useTranslation([], {
    keyPrefix: 'team.Modals.Vendors.createAndEdit.Add Vendor',
  });

  const {
    mode,
    openVendorModal,
    vendorIdToEdit,
    updateCallback,
    createCallback,
    loading,
  } = useSelector(({ vendorModalSlice }) => vendorModalSlice);
  const { team } = useSelector(({ userSlice }) => userSlice);

  const [tab, setTab] = useState(0);

  const [vendorTerms, setVendorTerms] = useState<VendorTerms[]>([]);

  const dispatch = useDispatch();

  const errorTabTrigger = (tabsWithError: number[]) => {
    if (tabsWithError.length && tabsWithError[0] !== undefined)
      setTab(tabsWithError[0]);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTab(newValue);
  };

  const resetState = () => {
    setTab(0);
    formik.resetForm();
  };

  const handleClose = () => {
    if (updateCallback) updateCallback(undefined);
    if (createCallback) createCallback(undefined);
    dispatch(closeModal());
    resetState();
  };

  const updateLoading = (value: boolean) => {
    dispatch(setLoading(value));
  };

  const exitViewMode = async () => {
    dispatch(setMode(VendorModalMode.EDIT));
  };

  const handleSave = async (payload: VendorCreate) => {
    updateLoading(true);
    if (mode == VendorModalMode.EDIT) {
      // check if modal is in edit mode
      if (vendorIdToEdit) {
        // double check if id exists. Should never happen.
        try {
          const updatedVendor = await client.updateVendor.mutate({
            ...payload,
            VendorId: vendorIdToEdit,
          });
          if (updateCallback) updateCallback(updatedVendor);
          dispatch(
            pushToast({
              message: `Vendor ${updatedVendor?.details.companyName || ''} updated`,
              additionalInfo: serverLatencyMessage,
              type: 'success',
              duration: 7000,
            }),
          );
        } catch (error) {
          console.error('error', error);
          if (error instanceof TRPCClientError) {
            dispatch(pushToast({ message: error.message, type: 'error' }));
          } else {
            dispatch(pushToast({ message: serverError, type: 'error' }));
          }
        }
      } else {
        // this should never happaned but its good to have this
        console.log('ERROR: Vendor id is wrong or missing: ', vendorIdToEdit);
        dispatch(pushToast({ message: serverError, type: 'error' }));
      }
    } else {
      try {
        if (team === 'init' || team === 'loading') return;
        const newVendor: Vendor = await client.createVendor.mutate({
          ...payload,
        });
        if (createCallback) createCallback(newVendor);
        dispatch(
          pushToast({
            message: `Vendor ${newVendor?.details.companyName || ''} created`,
            additionalInfo: serverLatencyMessage,
            type: 'success',
            duration: 7000,
          }),
        );
      } catch (error) {
        if (error instanceof TRPCClientError) {
          dispatch(pushToast({ message: error.message, type: 'error' }));
        } else {
          dispatch(pushToast({ message: serverError, type: 'error' }));
        }
      }
    }
    handleClose();

    updateLoading(false);
  };

  // Formik initialization
  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: vendorSchema,
    onSubmit: handleSave,
  });
  useFormikTabErrorMiddleware(formik, TAB_ERROR_PREFIXES, errorTabTrigger);

  useEffect(() => {
    const fetchData = async () => {
      updateLoading(true);
      if (vendorIdToEdit) {
        try {
          const vendor = await dispatch(fetchVendorToEdit(vendorIdToEdit));
          formik.setValues({
            ...formik.values, // Keeping any other existing form values
            ...vendor, // Seting vendor data returned from API
          });
        } catch (error) {
          handleClose();

          if (error instanceof TRPCClientError) {
            dispatch(pushToast({ message: error.message, type: 'error' }));
          } else {
            dispatch(pushToast({ message: serverError, type: 'error' }));
          }
        }
      }
      updateLoading(false);
    };
    fetchData();
  }, [vendorIdToEdit]);

  useEffect(() => {
    if (!openVendorModal) return;
    const fetchData = async () => {
      updateLoading(true);
      // Get Vendor terms
      try {
        const resp = await client.allVendorTerms.query();
        setVendorTerms(resp.results);
      } catch (error) {
        if (error instanceof TRPCClientError) {
          dispatch(pushToast({ message: error.message, type: 'error' }));
        } else {
          dispatch(pushToast({ message: serverError, type: 'error' }));
        }
        handleClose();
      }
      updateLoading(false);
    };
    fetchData();
  }, [openVendorModal]);

  const renderTitle = () => {
    switch (mode) {
      case VendorModalMode.EDIT:
        return tAddC('Edit Vendor');
      case VendorModalMode.VIEW:
        return tAddC('View Vendor');
      case VendorModalMode.CREATE:
        return tAddC('Add Vendor');
      default:
        return '';
    }
  };

  // This functions set default values
  useEffect(() => {
    if (vendorTerms.length > 0) {
      const defaultTerm = vendorTerms.find((vT) => vT.default);
      if (defaultTerm) {
        formik.setFieldValue('payment.vendorTerms', [defaultTerm]);
      }
    }
  }, [vendorTerms]); // Only run when `vendorTerms` changes

  // This just gets what tab has errors
  const tabsWithError = getTabsWithVisibleErrors(
    TAB_ERROR_PREFIXES,
    formik.errors,
    formik.touched,
  );

  return (
    <Dialog
      open={openVendorModal}
      onClose={(event, reason) => {
        if (reason && reason === 'backdropClick') return;
        handleClose();
      }}
      maxWidth="lg"
      scroll="paper"
      PaperProps={{ className: 'w-full' }}
      PaperComponent={DraggablePaper}
    >
      <DialogTitle
        id="draggable-dialog-title"
        sx={{ m: 0, p: 1, cursor: 'move' }}
      >
        {renderTitle()}
      </DialogTitle>
      <IconButton
        disabled={loading}
        color="primary"
        sx={{ position: 'absolute', right: 4, top: 4 }}
        onClick={handleClose}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent dividers sx={{ p: 1 }}>
        {loading ? (
          <Box
            height="400px"
            display="flex"
            justifyContent="center"
            alignItems="center"
          >
            <CircularProgress size={80} />
          </Box>
        ) : (
          <form onSubmit={formik.handleSubmit}>
            <Container>
              <Tabs
                centered
                value={tab}
                onChange={handleTabChange}
                sx={{
                  '& .MuiTabs-flexContainer': {
                    justifyContent: 'space-between',
                  },
                }}
                TabIndicatorProps={{
                  sx: tabIndicatorErrorSx(tabHasError(tab, tabsWithError)),
                }}
              >
                <Tab
                  sx={[
                    { fontWeight: 'bold', width: '100%' },
                    tabErrorSx(tabHasError(0, tabsWithError)),
                  ]}
                  label="Vendor Details & Payment Information"
                  {...a11yProps(0)}
                />
                <Tab
                  sx={[
                    { fontWeight: 'bold', width: '100%' },
                    tabErrorSx(tabHasError(1, tabsWithError)),
                  ]}
                  label="Accounts Receivable & Shipping"
                  {...a11yProps(1)}
                />
                <Tab
                  sx={[
                    { fontWeight: 'bold', width: '100%' },
                    tabErrorSx(tabHasError(2, tabsWithError)),
                  ]}
                  label="Contacts & Notes"
                  {...a11yProps(2)}
                />
              </Tabs>
            </Container>
            <TabPanel value={tab} index={0}>
              <VendorAndPayment
                formik={formik}
                vendorTerms={vendorTerms}
                viewMode={mode == VendorModalMode.VIEW}
              />
            </TabPanel>
            <TabPanel value={tab} index={1}>
              <ReceivableAndShipping
                formik={formik}
                viewMode={mode == VendorModalMode.VIEW}
              />
            </TabPanel>
            <TabPanel value={tab} index={2}>
              <ContactAndNotes
                formik={formik}
                viewMode={mode == VendorModalMode.VIEW}
              />
            </TabPanel>
          </form>
        )}
      </DialogContent>
      <DialogActions>
        <Box width="100%" sx={{ display: 'flex', justifyContent: 'end' }}>
          <Button
            disabled={loading}
            variant="outlined"
            color="primary"
            sx={{ width: buttonWidth, marginRight: buttonMarginSpace }}
            onClick={handleClose}
          >
            {tGlobal('Cancel')}
          </Button>
          {mode == VendorModalMode.VIEW ? (
            <Button
              disabled={loading}
              variant="contained"
              color="primary"
              sx={{ width: buttonWidth }}
              onClick={() => {
                exitViewMode();
              }}
            >
              {loading ? <CircularProgress size={20} /> : tGlobal('Edit')}
            </Button>
          ) : (
            <Button
              disabled={loading}
              variant="contained"
              color="primary"
              sx={{ width: buttonWidth }}
              onClick={() => {
                formik.submitForm();
              }}
            >
              {loading ? <CircularProgress size={20} /> : tGlobal('Save')}
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
}
